import React, { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { formatDateDMY } from '../../utils/dateFormat';

/**
 * InvoicePrintView — printable Tax Invoice rendered into a hidden iframe and
 * sent to the browser's print dialog. Designed to print on top of an
 * official pre-printed letterhead — no company header / footer is rendered.
 */
const InvoicePrintView = ({ invoice, customer, company, formatAmount, watermark, preparedBy, approvers, onClose }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    doc.open();
    doc.write('<!DOCTYPE html><html><head><title>Tax Invoice</title></head><body><div id="root"></div></body></html>');
    doc.close();
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const root = iframe.contentDocument?.getElementById('root');
    if (!root) return;
    const reactRoot = createRoot(root);
    reactRoot.render(
      <PrintLayout
        invoice={invoice}
        customer={customer}
        company={company}
        formatAmount={formatAmount}
        watermark={watermark}
        preparedBy={preparedBy}
        approvers={approvers}
      />
    );
    // Wait for every image inside the iframe (logo, header, footer, signatures)
    // to finish loading BEFORE firing window.print(). Without this gate the
    // first print on a fresh modal can capture a half-rendered layout — only
    // the cached logo shows; header/footer images are still in-flight.
    let cancelled = false;
    const fireAfterImages = () => {
      const idoc = iframe.contentDocument;
      if (!idoc) return;
      const imgs = Array.from(idoc.images || []);
      const waiters = imgs.map((img) => (
        img.complete && img.naturalWidth > 0
          ? Promise.resolve()
          : new Promise((res) => {
              const done = () => res();
              img.addEventListener('load', done, { once: true });
              img.addEventListener('error', done, { once: true });
              // Hard cap per image so a single broken URL doesn't block print.
              setTimeout(done, 4000);
            })
      ));
      Promise.all(waiters).then(() => {
        if (cancelled) return;
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch {
          /* user can manually print */
        }
      });
    };
    // React commit hasn't necessarily attached <img> tags yet — defer one frame.
    const t = setTimeout(fireAfterImages, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
      try { reactRoot.unmount(); } catch { /* noop */ }
    };
  }, [invoice, customer, company, formatAmount]);

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" data-testid="invoice-print-modal">
      <div className="bg-white rounded shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col">
        <div className="px-4 py-2 border-b flex items-center justify-between">
          <h3 className="font-semibold">Print preview · Tax Invoice {invoice.invoice_number}</h3>
          <div className="flex gap-2">
            <button
              onClick={() => iframeRef.current?.contentWindow?.print()}
              className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              data-testid="invoice-print-btn"
            >
              🖨 Print / Save as PDF
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50"
              data-testid="invoice-print-close-btn"
            >
              Close
            </button>
          </div>
        </div>
        <iframe ref={iframeRef} title="Invoice print" className="flex-1 w-full bg-gray-200" />
      </div>
    </div>
  );
};

/* ---------- Number → English words (for amount-in-words line) ---------- */
const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

const numToWords = (num) => {
  if (num === 0) return 'zero';
  if (num < 20) return ones[num];
  if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
  if (num < 1000) return ones[Math.floor(num / 100)] + ' hundred' + (num % 100 ? ' ' + numToWords(num % 100) : '');
  if (num < 1000000) return numToWords(Math.floor(num / 1000)) + ' thousand' + (num % 1000 ? ' ' + numToWords(num % 1000) : '');
  if (num < 1000000000) return numToWords(Math.floor(num / 1000000)) + ' million' + (num % 1000000 ? ' ' + numToWords(num % 1000000) : '');
  return String(num);
};

const amountInWords = (amount, currencyCode = 'BHD') => {
  const decimals = currencyCode === 'BHD' ? 3 : 2;
  const fractionName = currencyCode === 'BHD' ? 'fils' : 'cents';
  const safe = Math.max(0, parseFloat(amount) || 0);
  const whole = Math.floor(safe);
  const frac = Math.round((safe - whole) * Math.pow(10, decimals));
  const wholeWords = numToWords(whole);
  let str = wholeWords + ' ' + currencyCode;
  if (frac > 0) str += ' and ' + numToWords(frac) + ' ' + fractionName;
  str += ' only';
  // Capitalise first letter
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/* ---------- Print layout ---------- */
const PrintLayout = ({ invoice, customer, company, formatAmount, watermark, preparedBy, approvers }) => {
  const subtotal = parseFloat(invoice.subtotal) || 0;          // Gross / before VAT
  const vatPct = parseFloat(invoice.vat_percentage) || 0;
  const vatAmt = parseFloat(invoice.vat_amount) || 0;
  const totalInvoiceAmount = subtotal + vatAmt;                // Total incl. VAT
  const discount = parseFloat(invoice.discount) || 0;
  const netInvoiceAmount = Math.max(0, totalInvoiceAmount - discount);
  const advance = parseFloat(invoice.advance_payment) || 0;
  const balanceDue = parseFloat(invoice.total_amount) || Math.max(0, netInvoiceAmount - advance);
  const currency = company?.currency || 'BHD';

  // Derive Payment Terms display
  const paymentTerms =
    invoice.payment_terms ||
    (advance >= totalInvoiceAmount && totalInvoiceAmount > 0
      ? '100% Advance'
      : customer?.credit_period_days != null
      ? `${customer.credit_period_days} Days Credit`
      : '-');

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'Segoe UI', Arial, sans-serif; color: #111; background: #fff; }
        .page {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          padding: 50mm 12mm 80mm 12mm;
          box-sizing: border-box;
          position: relative;
          font-size: 11px;
        }
        .doc-title { text-align: center; font-size: 15px; font-weight: 700; letter-spacing: 1px; margin: 0 0 6px; }
        .doc-title .ar { display: block; font-size: 11px; font-weight: 600; margin-top: 1px; }
        .meta-grid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 0; border: 1px solid #000; }
        .meta-cell { padding: 4px 6px; border-right: 1px solid #000; }
        .meta-cell:last-child { border-right: 0; }
        .meta-cell .lbl { font-size: 9.5px; color: #444; }
        .meta-cell .ar { font-size: 9px; color: #555; }
        .to-block p { margin: 1px 0; line-height: 1.3; font-size: 10.5px; }
        .to-block .name { font-weight: 700; font-size: 11.5px; }
        .right-meta table { width: 100%; border-collapse: collapse; font-size: 10px; }
        .right-meta td { padding: 2px 4px; border-bottom: 1px dotted #bbb; vertical-align: top; }
        .right-meta td.l { width: 55%; color: #333; }
        .right-meta td.l .ar { display: block; font-size: 9px; color: #777; }
        .right-meta td.v { font-weight: 600; }
        .strip { display: grid; grid-template-columns: repeat(4, 1fr); border: 1px solid #000; border-top: 0; font-size: 10.5px; }
        .strip > div { padding: 3px 6px; border-right: 1px solid #000; }
        .strip > div:last-child { border-right: 0; }
        .strip .lbl { display: block; font-size: 9px; color: #555; text-transform: uppercase; }
        .strip .ar { font-size: 8.5px; color: #777; }
        .strip .val { font-weight: 600; }

        table.items { width: 100%; border-collapse: collapse; margin-top: 6px; }
        table.items th, table.items td { border: 1px solid #000; padding: 3px 5px; font-size: 10.5px; vertical-align: top; line-height: 1.3; }
        table.items th { background: #f3f4f6; text-align: left; font-size: 10px; }
        table.items th .ar { display: block; font-size: 8.5px; color: #555; font-weight: 500; }
        table.items td.num { text-align: right; font-variant-numeric: tabular-nums; }
        table.items td.center { text-align: center; }

        .totals-wrap { display: grid; grid-template-columns: 1fr 290px; gap: 10px; margin-top: 4px; }
        .words { border: 1px solid #000; padding: 5px 8px; font-size: 10.5px; }
        .words .label { font-size: 9px; color: #555; text-transform: uppercase; }
        .words .ar { font-size: 9px; color: #777; }
        table.totals { width: 100%; border-collapse: collapse; }
        table.totals td { border: 1px solid #000; padding: 3px 6px; font-size: 10.5px; }
        table.totals td.l { color: #222; }
        table.totals td.l .ar { display: block; font-size: 8.5px; color: #777; }
        table.totals td.v { text-align: right; font-variant-numeric: tabular-nums; width: 100px; }
        table.totals tr.grand td { background: #fff; color: #111; font-weight: 800; font-size: 14px; padding: 6px 8px; letter-spacing: 0.3px; border: 2px solid #000; }
        table.totals tr.grand td.l .ar { color: #555; }

        .remit { margin-top: 5px; font-size: 10.5px; }
        .remit .ar { color: #555; font-size: 9px; }
        .bank { margin-top: 4px; padding-top: 4px; border-top: 1px dashed #999; font-size: 12.5px; font-weight: 700; line-height: 1.55; color: #111; }
        .bank b { color: #111; font-weight: 800; }

        .signs { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: auto; padding-top: 24px; }
        .sign-box { display: flex; flex-direction: column; justify-content: flex-end; font-size: 10px; }
        .sign-box .stamp { height: 18mm; display: flex; align-items: flex-end; justify-content: center; margin-bottom: 1.5mm; }
        .sign-box .stamp img { max-height: 18mm; max-width: 90%; object-fit: contain; }
        .sign-box .stamp-dual { gap: 4mm; }
        .sign-box .stamp-dual .stamp-slot { flex: 1; display: flex; align-items: flex-end; justify-content: center; height: 100%; }
        .sign-box .stamp-dual .stamp-slot img { max-height: 18mm; max-width: 100%; object-fit: contain; }
        .sign-box .line { border-top: 1px solid #000; padding-top: 4px; text-align: center; }
        .sign-box .lbl { font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; font-size: 9.5px; color: #555; }
        .sign-box .ar { font-size: 9px; color: #666; }
        .sign-box .date { margin-top: 8px; font-size: 9.5px; color: #555; text-align: center; }

        /* Letterhead-clearance layout — applied to both screen preview and
           print so the on-screen modal matches the printed page exactly. */
        .wm {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
          z-index: 5;
        }
        .wm span {
          font-weight: 900;
          font-size: 180pt;
          color: rgba(180,30,30,0.10);
          transform: rotate(-30deg);
          letter-spacing: 0.1em;
          white-space: nowrap;
        }
        /* Header banner image — TOP-RIGHT corner, height-matched with the left logo. */
        .banner-top, .banner-bottom { position: absolute; z-index: 1; }
        .banner-top {
          top: 8mm; right: 12mm;
          width: 60mm; height: 28mm;
          display: flex; align-items: center; justify-content: flex-end;
        }
        .banner-bottom { bottom: 0; left: 0; right: 0; text-align: center; }
        .banner-top img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
        .banner-bottom img { width: 100%; max-height: 22mm; object-fit: contain; display: block; }
        /* Left-corner company logo + CR / VAT registration lines. */
        .logo-left {
          position: absolute;
          top: 8mm; left: 12mm;
          width: 86mm;
        }
        .logo-left .logo-box {
          width: 60mm; height: 28mm;
          display: flex; align-items: center; justify-content: flex-start;
        }
        .logo-left .logo-box img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
        .logo-left .reg-block { margin-top: 2mm; font-size: 9pt; color: #111; line-height: 1.45; }
        .logo-left .reg-block .lbl { color: #6b7280; }
        .logo-left .reg-block .val { font-weight: 700; color: #111; }

        @media print {
          /* margin: 0 on @page suppresses default browser headers/footers
             (date / page title / URL). Letterhead clearance handled via
             the .page padding above. */
          @page { size: A4; margin: 0; }
          html, body { margin: 0; padding: 0; }
          .banner-bottom { position: fixed !important; bottom: 0 !important; left: 0; right: 0; }
          .signs {
            position: fixed !important;
            bottom: 28mm !important;
            left: 12mm;
            right: 12mm;
            margin-top: 0 !important;
            padding-top: 0 !important;
          }
        }
      `}</style>

      <div className="page">
        {(company?.logo_url || company?.cr_number || company?.cr_number_2 || company?.tax_id) && (
          <div className="logo-left">
            {company.logo_url && (
              <div className="logo-box">
                <img src={company.logo_url} alt={company.name || ''} />
              </div>
            )}
            {(company.cr_number || company.cr_number_2 || company.tax_id) && (
              <div className="reg-block">
                {company.cr_number && (
                  <div><span className="lbl">CR No 1:</span> <span className="val">{company.cr_number}</span></div>
                )}
                {company.cr_number_2 && (
                  <div><span className="lbl">CR No 2:</span> <span className="val">{company.cr_number_2}</span></div>
                )}
                {company.tax_id && (
                  <div><span className="lbl">VAT No:</span> <span className="val">{company.tax_id}</span></div>
                )}
              </div>
            )}
          </div>
        )}
        {company?.document_header_url && (
          <div className="banner-top"><img src={company.document_header_url} alt="" /></div>
        )}
        {company?.document_footer_url && (
          <div className="banner-bottom"><img src={company.document_footer_url} alt="" /></div>
        )}
        {watermark && (
          <div className="wm"><span>{watermark}</span></div>
        )}
        <div className="doc-title">
          TAX INVOICE
          <span className="ar">فاتورة ضريبية</span>
        </div>

        {/* TO + Right meta */}
        <div className="meta-grid">
          <div className="meta-cell to-block">
            <div className="lbl">TO <span className="ar">/ الى</span></div>
            <p className="name">{invoice.customer_name}</p>
            {customer?.address && <p>{customer.address}</p>}
            {customer?.region && <p>{customer.region}</p>}
            {customer?.vat_reg_no && <p>VAT Reg No {customer.vat_reg_no}</p>}
            {customer?.cr_no && <p>CR No {customer.cr_no}</p>}
          </div>
          <div className="meta-cell right-meta">
            <table>
              <tbody>
                <tr>
                  <td className="l">Inv No <span className="ar">رقم الفاتورة</span></td>
                  <td className="v">{invoice.invoice_number}</td>
                </tr>
                <tr>
                  <td className="l">Date <span className="ar">التاريخ</span></td>
                  <td className="v">{formatDateDMY(invoice.invoice_date)}</td>
                </tr>
                <tr>
                  <td className="l">DO No <span className="ar">امر تسليم</span></td>
                  <td className="v">{invoice.do_no || ''}</td>
                </tr>
                <tr>
                  <td className="l">PO No <span className="ar">رقم امر الشراء</span></td>
                  <td className="v">{invoice.po_no || ''}</td>
                </tr>
                <tr>
                  <td className="l">PO Date <span className="ar">تاريخ امر الشراء</span></td>
                  <td className="v">{invoice.po_date ? formatDateDMY(invoice.po_date) : ''}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Strip: PROJECT | SALES PERSON | PAYMENT TERMS | LOCATION */}
        <div className="strip">
          <div>
            <span className="lbl">Project <span className="ar">المشروع</span></span>
            <span className="val">{invoice.project || '-'}</span>
          </div>
          <div>
            <span className="lbl">Sales Person <span className="ar">مندوب المبيعات</span></span>
            <span className="val">{invoice.sales_rep_name || '-'}</span>
          </div>
          <div>
            <span className="lbl">Payment Terms <span className="ar">طريقة الدفعات</span></span>
            <span className="val">{paymentTerms}</span>
          </div>
          <div>
            <span className="lbl">Location <span className="ar">الموقع</span></span>
            <span className="val">{customer?.region || invoice.location || '-'}</span>
          </div>
        </div>

        {/* Items table */}
        <table className="items">
          <thead>
            <tr>
              <th style={{ width: 28 }} className="center">#</th>
              <th style={{ width: 90 }}>Item Code<span className="ar">الرمز</span></th>
              <th>Product Description<span className="ar">وصف المنتج</span></th>
              <th style={{ width: 50 }} className="center">Unit<span className="ar">الوحدة</span></th>
              <th style={{ width: 50 }} className="num">Qty<span className="ar">الكمية</span></th>
              <th style={{ width: 80 }} className="num">Rate<span className="ar">السعر</span></th>
              <th style={{ width: 90 }} className="num">Total {currency}<span className="ar">المجموع</span></th>
            </tr>
          </thead>
          <tbody>
            {(invoice.items || []).map((it, i) => (
              <tr key={i}>
                <td className="center">{i + 1}</td>
                <td>{it.part_number || '-'}</td>
                <td>
                  <b>{it.product_name}</b>
                  {Array.isArray(it.serial_numbers) && it.serial_numbers.length > 0 && (
                    <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>
                      <b>S/N:</b> {it.serial_numbers.join(', ')}
                    </div>
                  )}
                </td>
                <td className="center">{it.unit || 'Pcs'}</td>
                <td className="num">{it.quantity}</td>
                <td className="num">{formatAmount(it.unit_price)}</td>
                <td className="num">{formatAmount(it.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Words + totals */}
        <div className="totals-wrap">
          <div className="words">
            <div className="label">Amount in words <span className="ar">/ المبلغ كتابةً</span></div>
            <div style={{ marginTop: 4, fontWeight: 600 }}>
              {amountInWords(balanceDue > 0 ? balanceDue : totalInvoiceAmount, currency)}
            </div>
            <div className="remit">
              Please remit the payment <span className="ar">/ يرجى تحويل المبلغ</span>
            </div>
            {(company?.bank_name || company?.iban) && (
              <div className="bank">
                {company?.bank_name && <div><b>Bank:</b> {company.bank_name}</div>}
                {company?.iban && <div><b>IBAN:</b> {company.iban}</div>}
              </div>
            )}
          </div>
          <table className="totals">
            <tbody>
              <tr>
                <td className="l">Gross Inv Amount<span className="ar">صافي مبلغ الفاتورة</span></td>
                <td className="v">{formatAmount(subtotal)}</td>
              </tr>
              <tr>
                <td className="l">{vatPct}% VAT Amount<span className="ar">قيمة الضريبة المضافة {vatPct}%</span></td>
                <td className="v">{formatAmount(vatAmt)}</td>
              </tr>
              <tr>
                <td className="l">Total Invoice Amount<span className="ar">مجموع مبلغ الفاتورة</span></td>
                <td className="v">{formatAmount(totalInvoiceAmount)}</td>
              </tr>
              <tr>
                <td className="l">Discount<span className="ar">الخصم</span></td>
                <td className="v">{discount > 0 ? formatAmount(discount) : ''}</td>
              </tr>
              <tr>
                <td className="l">Net Invoice Amount<span className="ar">صافي الفاتورة</span></td>
                <td className="v">{formatAmount(netInvoiceAmount)}</td>
              </tr>
              <tr>
                <td className="l">Advance Received<span className="ar">الدفعة المقدمة</span></td>
                <td className="v">{advance > 0 ? formatAmount(advance) : ''}</td>
              </tr>
              <tr className="grand">
                <td className="l">Balance Amount Due<span className="ar">الرصيد المتبقي</span></td>
                <td className="v">{formatAmount(balanceDue)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Signatures */}
        <div className="signs">
          <SigCellInv role="Prepared By" arabic="أعدها" person={preparedBy} dateStr={fmtDate(invoice?.invoice_date || invoice?.created_at)} />
          <ApprovedCellInv approvers={approvers} />
          <SigCellInv role="Received By" arabic="المستلم" person={null} />
        </div>
      </div>
    </>
  );
};

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s).slice(0, 10);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const SigCellInv = ({ role, arabic, person, dateStr }) => (
  <div className="sign-box">
    <div className="stamp">
      {person?.signature_url ? <img src={person.signature_url} alt="" /> : null}
    </div>
    <div className="line">
      <div className="lbl">{role} <span className="ar">/ {arabic}</span></div>
    </div>
    <div className="date">Date: {dateStr || '____________________'}</div>
  </div>
);

const ApprovedCellInv = ({ approvers }) => {
  const list = Array.isArray(approvers) ? approvers : [];
  if (list.length <= 1) {
    const p = list[0] || null;
    return <SigCellInv role="Approved By" arabic="اعتمدها" person={p} dateStr={fmtDate(p?.at)} />;
  }
  return (
    <div className="sign-box">
      <div className="stamp stamp-dual">
        {list.map((p, i) => (
          <div key={i} className="stamp-slot">
            {p?.signature_url ? <img src={p.signature_url} alt="" /> : null}
          </div>
        ))}
      </div>
      <div className="line">
        <div className="lbl">Approved By <span className="ar">/ اعتمدها</span></div>
      </div>
      <div className="date">
        Date:&nbsp;
        {list.map((p, i) => (
          <span key={i}>
            {i > 0 ? ' · ' : ''}{fmtDate(p?.at) || '—'}
          </span>
        ))}
      </div>
    </div>
  );
};

export default InvoicePrintView;
